import { OUTBOX_PREFIX } from "../auth/binding-wipe";

export interface OutboxOp {
  type: "upsert" | "delete";
  updatedAt: number;
  body?: unknown;
}

/**
 * The board-book writes of each local store that the kernel has not acked or refused yet:
 * the newest op per uuid, in localStorage under `OUTBOX_PREFIX + storeKey`.
 *
 * An op that storage refused (a full quota) is kept in memory and laid over the stored ops
 * per uuid. The stored ops of another window are never replaced by a copy from this one.
 */
// ponytail: localStorage, because the store schema belongs to the logbook library; a table there is the upgrade.
export class BoardbookOutbox {
  private readonly unsaved = new Map<string, Record<string, OutboxOp>>();

  constructor(private readonly storage: Pick<Storage, "getItem" | "setItem"> = localStorage) {}

  /** Every op held for the store, newest per uuid. */
  ops(key: string): Record<string, OutboxOp> {
    let stored: Record<string, OutboxOp> = {};
    try {
      const raw = this.storage.getItem(OUTBOX_PREFIX + key);
      if (raw) stored = (JSON.parse(raw) as Record<string, OutboxOp> | null) ?? {};
    } catch {
      // unreadable: the unsaved ops are all there is
    }
    const held = this.unsaved.get(key) ?? {};
    for (const [uuid, op] of Object.entries(held)) {
      // A newer op of another window replaced it, so it must not come back when that op settles.
      if (stored[uuid] && stored[uuid].updatedAt > op.updatedAt) delete held[uuid];
      else stored[uuid] = op;
    }
    return stored;
  }

  record(key: string, uuid: string, op: OutboxOp): void {
    if (this.write(key, { ...this.ops(key), [uuid]: op })) return;
    this.unsaved.set(key, { ...this.unsaved.get(key), [uuid]: op });
  }

  /** Drops the op of `uuid` when the kernel settled it at `updatedAt` or later. A newer op stays. */
  settle(key: string, uuid: string, updatedAt: number): void {
    const ops = this.ops(key);
    const op = ops[uuid];
    if (!op || op.updatedAt > updatedAt) return;
    delete ops[uuid];
    const held = this.unsaved.get(key);
    if (held?.[uuid] && held[uuid].updatedAt <= updatedAt) delete held[uuid];
    // On a failed write the stored op stays and is sent again; the kernel then answers lww or an equal ack.
    this.write(key, ops);
  }

  /** False when storage refused the write. */
  private write(key: string, ops: Record<string, OutboxOp>): boolean {
    try {
      this.storage.setItem(OUTBOX_PREFIX + key, JSON.stringify(ops));
      this.unsaved.delete(key);
      return true;
    } catch (err) {
      console.error("logbook outbox not persisted; it is lost on restart", err);
      return false;
    }
  }
}
